import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import Home from '../pages/HomePage';
import { destinationMiddleware } from '../server/middleware/destinationMiddleware';
import { getHandPickedSafaris } from '../server/getHandPickedSafaris';
import { getCountries } from '../server/getCountries';
import { getMapDataByCountry } from '../server/getMapDataByCountry';
import { getUsefulInfo } from '../server/getUsefulInfo';
import { sanitizeHtml } from '../server/utlis/sanitizeHtml';
import { generateOrganizationSchema } from '../utils/jsonLd';

const loadDestination = createServerFn()
  .middleware([destinationMiddleware])
  .handler(async ({ context }) => {
    return context.destination;
  });

export const Route = createFileRoute('/')({
  loader: async ({ context }) => {
    const destination = await loadDestination();
    const countryId = destination?.country || null;
    const destinationId = destination?._id || null;
    const [safaris, countriesList, usefulInfoData] =
      await Promise.all([
        destinationId
          ? getHandPickedSafaris({
              data: { destination: destinationId, limit: 3 },
            }).catch(() => [])
          : Promise.resolve([]),
        getCountries().catch(() => []),
        destinationId
          ? getUsefulInfo({
              data: { destinationId, page: 1, limit: 9 },
            }).catch(() => ({}))
          : Promise.resolve({}),
      ]);
    const usefulInfoArticles = usefulInfoData?.docs || [];

    const maraGuideArticles = destination?.maraGuide?.articles?.length
      ? await Promise.all(
          destination.maraGuide.articles.map(async (article) => ({
            ...article,
            description: await sanitizeHtml({
              data: { html: article.description },
            }).catch(() => article.description),
          }))
        )
      : [];

    const aboutDescription = destination?.about?.description
      ? await sanitizeHtml({
          data: { html: destination.about.description },
        }).catch(() => destination.about.description)
      : null;

    const finalCtaSubtext = destination?.finalCta?.subtext
      ? await sanitizeHtml({
          data: { html: destination.finalCta.subtext },
        }).catch(() => destination.finalCta.subtext)
      : null;

    const totalProperties = destination?.totalProperties || null;
    return {
      destination,
      safaris,
      countriesList,
      maraGuideArticles,
      usefulInfoArticles,
      aboutDescription,
      finalCtaSubtext,
      totalProperties,
    };
  },
  // Server-rendered via TanStack Router's <HeadContent /> — react-helmet-async
  // only patches document.head after client hydration, so non-JS crawlers
  // and link-unfurling bots (Slack, WhatsApp, Twitter/X, Facebook, LinkedIn)
  // never saw these tags.
  head: ({ loaderData }) => {
    const destination = loaderData?.destination;
    const title =
      destination?.metaData?.homePage?.title ||
      `${destination?.name || 'Maasai Mara'} | Book Accommodation and Safaris Directly`;
    const description =
      destination?.metaData?.homePage?.description ||
      `Find your preferred places to stay or your dream safari in ${destination?.name || 'Maasai Mara'} and contact them directly. We support direct bookings where you are in charge. Contact properties and safari operators directly.`;
    const image = destination?.images?.home?.[0]?.url;
    const siteUrl = (destination?.url || 'https://www.maasaimara.com').replace(/\/$/, '');
    const canonicalUrl = `${siteUrl}/`;

    const organizationSchema = generateOrganizationSchema(siteUrl);

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: canonicalUrl },
        ...(image
          ? [
              { property: 'og:image', content: image },
              { property: 'og:image:type', content: 'image/jpeg' },
            ]
          : []),
      ],
      links: [
        { rel: 'canonical', href: canonicalUrl },
        ...(image ? [{ rel: 'preload', as: 'image', href: image, fetchPriority: 'high' }] : []),
      ],
      scripts: [
        ...(organizationSchema
          ? [{ type: 'application/ld+json', children: JSON.stringify(organizationSchema) }]
          : []),
      ],
    };
  },
  component: Home,
  // ── Caching config
  staleTime: 5 * 60 * 1000, // reuse cached data for 5 mins on re-navigation
  gcTime: 10 * 60 * 1000, // keep in memory for 10 mins after route unmounts
});
